// 잠긴 견적 편집기의 판정 — 열려 있던 포털 창은 닫고, 확인 동작·진행 중 칸 편집은 반영하지 않는다.

import type { Dispatch, KeyboardEvent, MouseEvent } from "react";

import type { EditMode } from "@buildpy/core/modules/quote/editor/sheet/keymap";
import type { EditorAction } from "@buildpy/core/modules/quote/editor/types";

// 잠기면 열림 상태와 상관없이 닫힌 창으로 그린다(잠김은 풀리지 않는다).
export const openUnlessLocked = (open: boolean, locked: boolean): boolean => open && !locked;

// 부르는 순간 잠겨 있으면 동작을 버린다 — 닫히는 사이에 눌린 확인 단추·늦은 콜백 막이.
export const unlessLocked =
  <A extends unknown[]>(isLocked: () => boolean, fn: (...args: A) => void) =>
  (...args: A): void => {
    if (!isLocked()) fn(...args);
  };

// 렌더 때 받은 disabled 와 부르는 순간의 편집기 잠김(lockedRef)을 함께 본다 — 재렌더 전 클릭·blur 도 잡는다.
export const lockedNow = (disabled: boolean, isLocked: () => boolean) => (): boolean => disabled || isLocked();

// 보기만 바꾸는 액션(공종 접기) — 잠긴 동안에도 보낸다.
const VIEW_ACTIONS: ReadonlySet<EditorAction["type"]> = new Set<EditorAction["type"]>(["TOGGLE_TRADE"]);

// 편집 액션은 부르는 순간 잠겨 있으면 버리고, 보기 액션은 그대로 보낸다.
export const unlessLockedEdit =
  (isLocked: () => boolean, dispatch: Dispatch<EditorAction>) =>
  (action: EditorAction): void => {
    if (VIEW_ACTIONS.has(action.type) || !isLocked()) dispatch(action);
  };

// 잠긴 편집기의 <fieldset disabled> 가 못 끄는 span 단추 속성 — Enter 는 누를 때, Space 는 뗄 때(InfoTip 과 같은 규칙).
export const fieldsetFreePress = (onPress: () => void) => ({
  role: "button" as const,
  tabIndex: 0,
  // label 조상이 있어도 그 컨트롤로 클릭이 넘어가지 않게 기본 동작을 막는다(InfoTip 과 같은 방어).
  onClick: (e: MouseEvent) => {
    e.preventDefault();
    onPress();
  },
  onKeyDown: (e: KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    e.stopPropagation();
    if (e.key === "Enter" && !e.repeat) onPress();
  },
  onKeyUp: (e: KeyboardEvent) => {
    if (e.key !== " ") return;
    e.preventDefault();
    e.stopPropagation();
    onPress();
  },
});

// 잠김이 없는 화면(설정의 템플릿 편집)에서 쓰는 isLocked.
export const NEVER_LOCKED = (): boolean => false;

// 시트 이동·Tab 이 칸 편집을 확정하는지 — 잠긴 동안은 확정하지 않고 옮기기만 한다.
export const commitsOnMove = (locked: boolean): boolean => !locked;

// 잠기는 순간 진행 중이던 칸 편집을 버려야 하는지.
export const cancelsEditOnLock = (locked: boolean, mode: EditMode): boolean => locked && mode !== "idle";

// 카드 칸이 꺼지는(잠기는) 순간 쓰던 글자를 버려야 하는지 — 확정·되돌리기 없이 들어올 때 값으로.
export const dropsEditWhenOff = (off: boolean, editing: boolean): boolean => off && editing;
